/**
 * Desktop (`lg` and up) image-beside-text teaser: the image on the left half, then title, text
 * and a "More about" arrow link on the right. Shared by the Product Quality, Why Metal Matters
 * and Environment, Health & Safety teasers on the Ryerson Commitment landing page (see the header
 * of `./ProductQuality.tsx`).
 */
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import React from "react";
import { Button } from "@ryerson/frontend.button";
import { Link } from "@ryerson/frontend.navigation";
import { ContentSection, Flex } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { ProductQualityProps } from "./ProductQuality";

const Title = styled(Typography)`
	margin: 0px;
`;

const Content = styled(Typography)`
	margin: 0px;
	padding-top: 56px;
	line-height: 22px;
`;

const Section = styled(Flex)`
	position: relative;
	width: 50%;
`;

const ButtonContainer = styled(Flex)`
	padding-top: 56px;
`;

const contentSectionPadding = css`
	padding-left: 140px;
`;

const MoreAboutLabel = styled(Typography)`
	margin-left: 20px;
	white-space: pre-line;
`;

/** Renders one teaser; the whole button-and-label row is the link. */
const ProductQualityDesktop: React.FC<ProductQualityProps> = ({ ...props }) => {
	const { theme } = useTheme();
	const background = props?.background || "secondary";
	const { primaryBrand, secondaryBrand } = theme.colors.primary;

	return (
		// Anchor id: the title lower-cased, whitespace runs to `-`, as in `Shared/AllSections`.
		<div id={props.title.toLocaleLowerCase().replace(/\s+/g, "-")}>
			<ContentSection vPadding="141px" type={background}>
				<Flex>
					<Section>
						<img src={props?.imageUrl} width="100%" height="360px" alt={props.title} />
					</Section>
					<Section direction="column" css={contentSectionPadding}>
						<Title variant="h2" type={background}>
							{props?.title}
						</Title>
						<Content>{props?.content}</Content>
						<Link to={props.moreAboutLink}>
							<ButtonContainer alignItems="center">
								<Button
									shape="circle"
									label=""
									leftIcon="forward"
									foreground={"light"}
									onClick={() => {}}
									background={primaryBrand}
									size="sm"
								/>
								<MoreAboutLabel color={secondaryBrand} size="sm" weight="bold">
									{props?.buttonLabel}
								</MoreAboutLabel>
							</ButtonContainer>
						</Link>
					</Section>
				</Flex>
			</ContentSection>
		</div>
	);
};

export default ProductQualityDesktop;
